import { React, PropTypes, reduxConnect, styled } from '../lib/imports';
import { setMenuLinkClicked } from '../routing/actions';
import { ColorProvider, Image, Spacing, Text } from '../components';
import { useCdnUrl, useCdnImage, getAssetSet } from '../app/context/config';
import { NavLink } from 'react-router-dom';
import { css } from '@emotion/core';
import { useSelector } from 'react-redux';
import { fireMessageEvent } from '../messenger/utils';
import { stylingHelpers } from '../designTokens';
import { analyticActionMap } from '../analytics/utils';

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const { textByte, textPeta, textGiga, textTera, flexCenter, disableVisually } = stylingHelpers;

const BlurbContainer = styled.div`
  background: ${({ theme }) => theme.colors.darkBg} no-repeat top / 100% 266px
    url(${assetSet('challengeCardBackground')});
`;
const GameInfoList = styled.ol`
  list-style: none;
  margin: ${({ theme }) => theme.spacings.mega} 0 0;
  padding: 0 0 ${({ theme }) => theme.spacings.tera} 0;
`;

const GameInfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacings.giga};
`;
const GameInfoItemImage = styled.div`
  width: 82px;
  flex-shrink: 0;
  margin-right: ${({ theme }) => theme.spacings.mega};
`;
const GameInfoItemText = styled.p`
  flex-grow: 1;
  ${({ theme }) => theme.typography.text.tera}
`;

const HCLogo = styled(Image)`
  width: 50%;
  padding-top: ${({ theme }) => theme.spacings.giga};
`;

const BORDER_WIDTH = '1px';

const baseStyles = ({ theme }) => css`
  margin: ${theme.spacings.atom} auto;
  text-decoration: none;
  font-family: ${theme.fontStack.bold.name};
  font-weight: ${theme.fontStack.bold.weight};
  color: ${theme.colors.white};
  background-color: ${theme.colors.red};
  border: 1px solid ${theme.colors.red};
  border-radius: ${theme.borderRadius.peta};
  cursor: pointer;
  padding: ${theme.spacings.kilo} ${theme.spacings.mega};
  position: relative;
  min-width: 170px;

  ${textPeta({ theme })}
  ${flexCenter({ theme })}

  &:focus,
  &:active {
    outline: none;
  }

  &:disabled,
  &[disabled] {
    ${disableVisually()};
    background: ${theme.colors.n700};
    border-color: ${theme.colors.n700};
    color: ${theme.colors.white};
  }
`;

const sizeStyles = ({ theme, hasIcon, size = 'tera' }) => {
  const sizeMap = {
    byte: {
      padding: `${theme.spacings.bit} ${theme.spacings.byte}`,
      borderRadius: theme.borderRadius.peta,
      type: textByte({ theme }),
    },
    giga: {
      padding: `${theme.spacings.bit} calc(${theme.spacings.giga} - ${BORDER_WIDTH})`,
      borderRadius: theme.borderRadius.peta,
      type: textGiga({ theme }),
    },
    peta: {
      padding: `${theme.spacings.mega}`,
      borderRadius: theme.borderRadius.peta,
      type: textTera({ theme }),
    },
    mega: {
      borderRadius: theme.borderRadius.peta,
      maxWidth: '170px',
    },
    tera: {
      borderRadius: theme.borderRadius.peta,
      type: textTera({ theme }),
      minWidth: '230px',
    },
  };

  const { type, ...rest } = sizeMap[size] || 'byte';

  return css`
    ${type};
    ${rest};
  `;
};

const StyledLink = styled(NavLink)(baseStyles, sizeStyles);

const { clickedType, path, buttonType, makeYourPicks, gameInfo } = analyticActionMap;

const HowTo = ({ headerSrc, blurb, steps, btnText, prizesHeadline, handleLinkClick, playNow }) => {
  const cdnUrl = useCdnUrl();
  const { config } = useSelector((state) => state);
  const { env, platform } = config;

  const handleMakeYourPicksClick = () => {
    fireMessageEvent({
      platform,
      env,
      title: clickedType,
      data: {
        path: path,
        modal: gameInfo,
        element: buttonType,
        element_name: makeYourPicks,
      },
    });
    handleLinkClick(playNow);
  };
  return (
    <BlurbContainer>
      <HCLogo src={`${cdnUrl}${headerSrc}`} />
      <Spacing sizeLeft='tera' sizeRight='tera' paddingBottom='tera'>
        <ColorProvider variant='white'>
          {blurb.map((str, i) => (
            <Text centered size='exa' key={`blurb-${i}`}>
              {str}
            </Text>
          ))}
          <GameInfoList>
            {steps.map((step, i) => (
              <GameInfoItem key={`logo-${i + 1}`}>
                <GameInfoItemImage>
                  <Image src={`${cdnUrl}${step.imgSrc}`} />
                </GameInfoItemImage>
                <GameInfoItemText dangerouslySetInnerHTML={{ __html: step.text }} />
              </GameInfoItem>
            ))}
          </GameInfoList>
          <Text centered size='tera'>
            {prizesHeadline}
          </Text>
        </ColorProvider>
        <StyledLink disabled={false} to='/' size='mega' onClick={handleMakeYourPicksClick}>
          {btnText}
        </StyledLink>
      </Spacing>
    </BlurbContainer>
  );
};

HowTo.propTypes = {
  headerSrc: PropTypes.string,
  blurb: PropTypes.arrayOf(PropTypes.string),
  prizesHeadline: PropTypes.string,
};

const mapStateToProps = (state) => {
  const { strings, routing } = state;
  const { playNow } = routing.routeById;
  const allStrings = {
    blurb: strings['howTo/hockeyChallenge/blurb/text'],
    steps: strings['howTo/hockeyChallenge/blurb/steps'].map((str, i) => ({
      imgSrc: strings['howTo/hockeyChallenge/steps/image/src'][i],
      text: str,
    })),
    btnText: strings['howTo/hockeyChallenge/blurb/button/text'],
    prizesHeadline: strings['howTo/hockeyChallenge/prizes/text'],
  };

  return {
    headerSrc: strings['howTo/hockeyChallenge/header/image/src'],
    playNow: playNow.id,
    ...allStrings,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleLinkClick: (id) => dispatch(setMenuLinkClicked(id)),
  };
};

export default reduxConnect(mapStateToProps, mapDispatchToProps)(HowTo);
